import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { trpc } from "@/lib/trpc";
import { Loader2, Scale } from "lucide-react";
import { toast } from "sonner";

/**
 * Reconciliation is intentionally account-scoped: every displayed balance chain and
 * every adjustment belongs to exactly one bank account. "All Accounts" only groups
 * the independent statuses; it never creates a combined adjustment.
 */
export function AccountReconciliationPanel() {
  const { t, language, isRTL } = useLanguage();
  const utils = trpc.useUtils();
  const { data: reconciliation, isLoading } = trpc.transaction.reconciliationStatus.useQuery();
  const accountStatuses = reconciliation?.accounts ?? [];
  const hasDifference = accountStatuses.some((account) => account.state === "different");

  const reconcile = trpc.transaction.reconcile.useMutation({
    onSuccess: () => {
      toast.success(t("reconcile.adjustmentCreated"));
      void utils.transaction.reconciliationStatus.invalidate();
      void utils.transaction.list.invalidate();
      void utils.transaction.summary.invalidate();
      void utils.transaction.latestBalance.invalidate();
    },
    onError: (error: { message: string }) => toast.error(error.message),
  });

  const handleReconcile = (account: typeof accountStatuses[number]) => {
    if (account.state !== "different" || account.gap === null) return;
    reconcile.mutate({
      accountNumber: account.accountNumber,
      notes: language === "ar"
        ? `تعديل تسوية تلقائي للحساب ${account.accountNumber}: الفجوة ${Math.abs(account.gap).toFixed(3)} د.ك`
        : `Auto reconciliation adjustment for ${account.accountNumber}: gap of ${Math.abs(account.gap).toFixed(3)} KWD`,
    });
  };

  if (isLoading || accountStatuses.length === 0) return null;

  return (
    <section id="balance-status" aria-labelledby="account-reconciliation-title" dir={isRTL ? "rtl" : "ltr"}>
      <Card className={hasDifference ? "border-blue-200 dark:border-blue-800" : "border-border"}>
        <CardHeader className="px-3 pb-2 pt-3 sm:px-6 sm:pb-3 sm:pt-6">
          <CardTitle id="account-reconciliation-title" className="flex items-center gap-2 text-sm sm:text-base">
            <Scale className="h-4 w-4 text-blue-500" />
            {t("reconcile.title")}
          </CardTitle>
          <p className="mt-1 text-[10px] text-muted-foreground sm:text-xs">{t("reconcile.allAccountsDesc")}</p>
        </CardHeader>
        <CardContent className="space-y-3 px-3 pb-3 sm:px-6 sm:pb-6">
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">{t("reconcile.allAccountsSummary")}</div>
          <div className="space-y-2">
            {accountStatuses.map((account) => {
              const hasGap = account.state === "different" && account.gap !== null;
              const isPositiveGap = (account.gap ?? 0) > 0;
              return (
                <div key={account.accountNumber} className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">{account.accountNumber}</p>
                    <Badge variant={account.state === "reconciled" ? "secondary" : account.state === "different" ? "destructive" : "outline"} className="text-xs">
                      {account.state === "reconciled" ? t("reconcile.balanced") : account.state === "different" ? t("reconcile.differenceFound") : t("reconcile.notComparable")}
                    </Badge>
                  </div>
                  {account.hasData ? (
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div><p className="text-[10px] text-muted-foreground">{t("reconcile.smsBalance")}</p><p className="whitespace-nowrap text-xs font-semibold tabular-nums">KWD {account.latestBalance?.toFixed(3)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">{t("reconcile.calculatedBalance")}</p><p className="whitespace-nowrap text-xs font-semibold tabular-nums">KWD {account.calculatedBalance?.toFixed(3)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">{t("reconcile.totalCashIn")}</p><p className="whitespace-nowrap text-xs font-semibold tabular-nums" style={{ color: "var(--income)" }}>KWD {account.totalCashIn?.toFixed(3)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">{t("reconcile.totalCashOut")}</p><p className="whitespace-nowrap text-xs font-semibold tabular-nums" style={{ color: "var(--expense)" }}>KWD {account.totalCashOut?.toFixed(3)}</p></div>
                    </div>
                  ) : <p className="mt-2 text-xs text-muted-foreground">{t("reconcile.notComparableDesc")}</p>}
                  {hasGap ? (
                    <div className="mt-3 flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30 sm:flex-row sm:items-center sm:justify-between">
                      <div><p className="text-xs font-medium text-foreground">{t("reconcile.gap")}: {isPositiveGap ? t("reconcile.missingIncome") : t("reconcile.missingExpense")}</p><p className="text-[10px] text-muted-foreground">{t("reconcile.explanation")}</p></div>
                      <Badge variant="secondary" className="w-fit text-xs font-bold tabular-nums" style={{ color: isPositiveGap ? "var(--income)" : "var(--expense)" }}>{isPositiveGap ? "+" : "-"}KWD {Math.abs(account.gap ?? 0).toFixed(3)}</Badge>
                    </div>
                  ) : null}
                  {hasGap ? <Button onClick={() => handleReconcile(account)} disabled={reconcile.isPending} className="mt-3 min-h-11 w-full gap-2 sm:w-auto"><>{reconcile.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}</>{t("reconcile.createAdjustment")}</Button> : null}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
